import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { FeedItem } from '@/components/news/types';

/**
 * Feed admin : masquer les activités des comptes internes et de test
 * (adresse @jeraisonne.fr, domaine de test, admins — règle serveur
 * `is_internal_account`, migration 0546). Réglage mémorisé sur l'APPAREIL,
 * activé par défaut.
 */
export const ADMIN_FEED_HIDE_INTERNAL_KEY = 'jr_admin_feed_show_internal';

export function useAdminFeedHideInternal(): [boolean, (next: boolean) => void] {
  const [hide, setHide] = useState(() => {
    try {
      return localStorage.getItem(ADMIN_FEED_HIDE_INTERNAL_KEY) !== '1';
    } catch {
      return true;
    }
  });
  const set = useCallback((next: boolean) => {
    setHide(next);
    try {
      if (next) localStorage.removeItem(ADMIN_FEED_HIDE_INTERNAL_KEY);
      else localStorage.setItem(ADMIN_FEED_HIDE_INTERNAL_KEY, '1');
    } catch {
      /* sans mémoire */
    }
  }, []);
  return [hide, set];
}

/** Identifiants des comptes internes et de test (admin seulement). */
export function useInternalAccountIds(enabled: boolean) {
  return useQuery({
    queryKey: ['admin-internal-account-ids'],
    enabled,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const db = supabase as unknown as SupabaseClient;
      const { data, error } = await db.rpc('admin_internal_account_ids');
      if (error) throw error;
      return new Set((data as string[] | null) ?? []);
    },
  });
}

/**
 * Personne dont l'élément raconte l'activité : l'élève pour un devoir (pas le
 * prof qui l'a donné), sinon l'auteur. `null` = rien à filtrer (blog, visio,
 * discussion, récap…). PUR.
 */
export function feedItemSubject(item: FeedItem): string | null {
  switch (item.kind) {
    case 'assignment':
      return item.data.student_id;
    case 'challenge':
      return item.data.user_id;
    case 'attempt':
    case 'attempt_started':
    case 'submission':
    case 'evaluation':
    case 'diagnostic':
      return item.data.student_id;
    case 'question_report':
      return item.data.reporter_id;
    default:
      return null;
  }
}

/**
 * Participants d'un échange (discussion, visio) : l'élément est masqué si l'un
 * des participants AUTRES que le viewer est interne. Une discussion de l'admin
 * avec un vrai élève reste donc visible. `null` = pas un échange. PUR.
 */
export function feedItemParticipants(item: FeedItem): string[] | null {
  switch (item.kind) {
    case 'discussion':
      return [item.data.tutorId, item.data.studentId];
    case 'visio':
      return [item.data.tutor_id, item.data.student_id].filter((id): id is string => !!id);
    default:
      return null;
  }
}

/**
 * Retire les éléments dont la personne est un compte interne ou de test, sauf
 * ceux du viewer lui-même (ses exercices, ses défis…). PUR.
 */
export function withoutInternal(
  items: FeedItem[],
  internal: ReadonlySet<string>,
  viewerId?: string | null,
): FeedItem[] {
  if (internal.size === 0) return items;
  return items.filter((it) => {
    const people = feedItemParticipants(it);
    if (people) return !people.some((id) => id !== viewerId && internal.has(id));
    const who = feedItemSubject(it);
    // Ses propres activités restent toujours visibles (l'admin est lui-même « interne »).
    return !who || who === viewerId || !internal.has(who);
  });
}
